import {useMemo} from 'react'
import {useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCustomerProductLists} from '@salesforce/commerce-sdk-react'

export const WISH_LIST_TYPE = 'wish_list'
export const CUSTOM_FOLDER_TYPE = 'custom_1'

/**
 * Fetches all product lists for the authenticated customer and separates
 * "My Favourites" (type: wish_list) from custom folders (type: custom_1).
 *
 * Returns undefined when the customer is not authenticated (no customerId).
 */
export const useProductLists = () => {
    const customerId = useCustomerId()

    const {data, isLoading, error, refetch} = useCustomerProductLists(
        {parameters: {customerId}},
        {enabled: !!customerId}
    )

    const allLists = data?.data ?? []

    const wishList = useMemo(
        () => allLists.find((list) => list.type === WISH_LIST_TYPE) ?? null,
        [allLists]
    )

    const customFolders = useMemo(
        () => allLists
            .filter((list) => list.type === CUSTOM_FOLDER_TYPE)
            .sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate)),
        [allLists]
    )

    /**
     * Total item count across ALL lists (My Favourites only — items in custom
     * folders also exist in My Favourites, so we avoid double-counting).
     */
    const totalItemCount = wishList?.customerProductListItems?.length ?? 0

    /**
     * Returns true if the customer already has a wish_list list created.
     * Used to guard against creating duplicates.
     */
    const hasWishList = wishList !== null

    return {
        allLists,
        wishList,
        customFolders,
        totalItemCount,
        hasWishList,
        isLoading,
        error,
        refetch
    }
}
